import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { Language } from "./types";
import { LanguageContext } from "./context";
import {
  getPageType,
  isInternalPage,
  isModalPage,
} from "../../shared/utils/getPageType";
import { navigate } from "gatsby";

const SUPPORTED_LANGUAGES: Language[] = ["en", "de"];

function getLanguageFromUrl(path: string): Language {
  const firstSegment = path.split("/")[1];
  return SUPPORTED_LANGUAGES.includes(firstSegment as Language)
    ? (firstSegment as Language)
    : "en";
}

export const LanguageProvider = ({ children }: PropsWithChildren) => {
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : undefined;

  // #TODO: temporary using mainLanguage for main page, remove when have different pages for languages
  const [mainLanguage, setMainLanguage] = useState<Language>("en");

  const language = useMemo(() => {
    if (pathname == null) return "en";

    if (isInternalPage(pathname) || isModalPage(pathname))
      return getLanguageFromUrl(pathname);

    if (getPageType(pathname) === "main") return mainLanguage;

    return "en";
  }, [pathname, mainLanguage]);

  const changeLanguage = useCallback(
    (lang: Language) => {
      if (pathname == null) return;

      if (getPageType(pathname) !== "main") {
        const newPathname =
          lang === "en"
            ? "/" + pathname.split("/").splice(2).join("/")
            : `/${lang}${pathname}`;
        navigate(newPathname, { replace: true });
      }

      setMainLanguage(lang);
    },
    [pathname],
  );

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
